import { createAsyncThunk, createSlice, createSelector } from '@reduxjs/toolkit';
import { fetchAnalyticsAggregation } from './analyticsAPI';

const REFRESH_CONFIG = {
  dynamic: ['sumChargeAgg', 'netLoadAgg', 'chargingInsightsAgg', 'buildingForecastAgg', 'summationsiteAgg', 'smartMeterAgg'],
  static: ['priceAgg']
};

const initialState = { data: null, status: 'idle', error: null };

export const fetchAnalyticsAggregationData = createAsyncThunk(
  'analyticsAggregation/fetchData',
  async ({ filters = {}, options = {} }, { getState, rejectWithValue }) => {
    try {
      const { data: existingData } = getState().analyticsAggregation;
      const allKeys = [...REFRESH_CONFIG.dynamic, ...REFRESH_CONFIG.static];

      // Efficiently build refresh map
      const refreshMap = allKeys.reduce((acc, key) => {
        acc[key] = REFRESH_CONFIG.dynamic.includes(key) || !existingData?.[key];
        return acc;
      }, {});

      const response = await fetchAnalyticsAggregation({ refresh: refreshMap, filters, options });
      
      if (!response?.data) return rejectWithValue('Empty response');

      // PERFORMANCE: Skip heavy validation loops. Just return the raw data.
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || 'Unknown error');
    }
  }
);

const analyticsSlice = createSlice({
  name: 'analyticsAggregation',
  initialState,
  reducers: {
    resetAnalyticsAggregation: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAnalyticsAggregationData.pending, (s) => {
        if (!s.data) s.status = 'loading';
        s.error = null;
      })
      .addCase(fetchAnalyticsAggregationData.fulfilled, (s, { payload }) => {
        s.status = 'succeeded';
        // MEMORY OPTIMIZATION: Shallow merge only. 
        // This prevents Immer from having to traverse and proxy the entire tree.
        s.data = s.data ? { ...s.data, ...payload } : payload;
      })
      .addCase(fetchAnalyticsAggregationData.rejected, (s, { payload }) => {
        s.status = 'failed';
        s.error = payload;
      });
  },
});

// --- High-Performance Memoized Selectors ---
const getSlice = (state) => state.analyticsAggregation;

export const selectAnalyticsData = (state) => getSlice(state).data;
export const selectAnalyticsStatus = (state) => getSlice(state).status;
export const selectAnalyticsError = (state) => getSlice(state).error;

/**
 * Optimized Factory Selector:
 * Extracts .data from the payload keys ONLY when that specific key changes.
 */
const createAggSelector = (key) => createSelector(
  [selectAnalyticsData],
  (data) => data?.[key]?.data || null
);

export const selectSumChargeAgg = createAggSelector('sumChargeAgg');
export const selectSmartMeterAgg = createAggSelector('smartMeterAgg');
export const selectPriceAgg = createAggSelector('priceAgg');
export const selectNetLoadAgg = createAggSelector('netLoadAgg');
export const selectChargingInsightsAgg = createAggSelector('chargingInsightsAgg');
export const selectBuildingForecastAgg = createAggSelector('buildingForecastAgg');
export const selectSummationsiteAgg = createAggSelector('summationsiteAgg');

export const { resetAnalyticsAggregation } = analyticsSlice.actions;
export default analyticsSlice.reducer;